import { useState, useEffect, useRef } from "react";
import { createFilterOptions, getId, isValidId, updateActiveTask, Util } from '../../util/Util'
import RefreshButton from "../../components/RefreshButton";
import { useUI } from "../../providers/MoleUIContext";
import { useAppSocket } from "../../providers/AppSocketContext";
import { MIN_DATE, MYSQL_LIMITS, INVENTORY_TYPES as types } from "../../util/constants";
import { useAuth } from "../../hooks/useAuth";
import { Autocomplete, Grid, TextField } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { DatePicker } from "../../components/MoleDatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useInventory } from "./InventoryContext";
import { businessTodayDate, parseISODay } from "../../util/businessClock";

interface Props {
	setValidateCheck: (value: boolean) => void;
}

const InventoryDetail = ({ setValidateCheck }: Props) => {
	const [depots, setDepots] = useState<any[]>([]);
	const [isClientLoading, setIsClientLoading] = useState<boolean>(false);
	const [isDepotLoading, setIsDepotLoading] = useState<boolean>(false);
	const [errorInput, setErrorInput] = useState("");
	const [clients, setClients] = useState<any[]>([]);

	const { setPendingTasks, activeTask } = useInventory();
	const { appSettings } = useAppSocket();
	const { queueReRun, t } = useUI();
	const { api } = useAuth();


	const prevSourceRef = useRef<number | null>(null);

	const minDate = appSettings?.SYSTEM_INCEPTION_DATE?.value || MIN_DATE;
	const itemsExist = (activeTask?.items || []).length > 0;
	const getInventoryTypeLabel = (item: { key: string; label: string }) =>
		t(`common:inventory.types.${item.key}`, { defaultValue: item.label });

	const fetchClients = async () => {
		setIsClientLoading(true);

		const res = await api.get("/clients",
			{
				params: {
					Active: true,
					Type: "WORKER"
				}
			}
		);

		if (res) {
			if (res.status === 200) {
				res.data.sort((a: any, b: any) => a.acc_name.localeCompare(b.acc_name));
				setClients(res.data);
			} else if (res.status === 401 || res.status === 422) {
				queueReRun(fetchClients);
			} else {
				console.error("Failed to fetch data:", res);
			}
		}

		setIsClientLoading(false);
	};

	const fetchDepots = async () => {
		setIsDepotLoading(true);

		const res = await api.get("/depot/meterred", { params: { Operation: "Inventory" } });

		if (res) {
			if (res.status === 200) {
				setDepots(res.data);
			} else if (res.status === 401 || res.status === 422) {
				queueReRun(fetchDepots);
			} else {
				console.error("Failed to fetch data:", res);
			}
		}

		setIsDepotLoading(false);
	};

	const onReceiverChanged = (receiver: number) => {
		updateActiveTask(setPendingTasks, { receiver });
	};

	const onRefChanged = (reference: string = Util.getConversionNumber()) => {
		updateActiveTask(setPendingTasks, { reference });
	};

	const onDateChanged = (taskDate: Date = businessTodayDate()) => {
		updateActiveTask(setPendingTasks, { date: Util.formatISODate(taskDate) });
	};

	const onDepotChanged = (depotId: number) => {
		updateActiveTask(setPendingTasks, { depot: depotId });
	};

	useEffect(() => {
		fetchClients();
		fetchDepots();
	}, []);



	useEffect(() => {
		if (clients.length > 0) {
			const receiverId = getId(clients, activeTask?.receiver);
			if (receiverId !== activeTask?.receiver) {
				onReceiverChanged(receiverId);
			}
		}
	}, [activeTask, clients]);

	useEffect(() => {
		if (activeTask) {
			const currentSource = activeTask.depot;
			if (prevSourceRef.current !== currentSource) {
				const depotName = depots.find((d) => d.id === currentSource)?.DepotName ?? "";

				if (depotName.trim().length !== 0 && (activeTask.description.trim().length === 0 || !Util.compareStrings(activeTask.description, depotName))) {
					updateActiveTask(setPendingTasks, { description: depotName });
				}
			}
			prevSourceRef.current = currentSource;
		}
	}, [activeTask]);

	useEffect(() => {
		const isValid = isValidId(clients, activeTask?.receiver) &&
			isValidId(depots, activeTask?.depot) && !!activeTask?.invType;
		setValidateCheck(isValid);
	}, [activeTask, clients, depots]);

	return (
		<Grid container spacing={0}>
			<Grid size={{ xs: 6, sm: 6, md: 3 }} >
				<Autocomplete
					id="clientInput"
					size="small"
					options={clients}
					filterOptions={createFilterOptions((opt) => opt.acc_name || "")}
					getOptionLabel={(client) => client.acc_name || ""}
					value={clients.find((c) => c.id === activeTask?.receiver) || null}
					onChange={(_, newValue) => {
						onReceiverChanged(newValue?.id || -1);
					}}
					loading={isClientLoading}
					autoHighlight
					autoComplete
					autoSelect
					disabled={!activeTask || isClientLoading}
					renderInput={(params) => (
						<TextField
							{...params}
							label={
								<span>
									{t("common:inventoryDetail.observer")}&nbsp;&nbsp;
									<RefreshButton isWorking={isClientLoading} onClick={fetchClients} disabled={isClientLoading} />
								</span>
							}
							variant="filled"
							fullWidth
						/>
					)}
				/>
			</Grid>
			<Grid size={{ xs: 6, sm: 6, md: 2 }} >
				<TextField
					id="referenceInput"
					size="small"
					label={
						<span>
							{t("common:supplyDetail.reference")}&nbsp;&nbsp;
							<RefreshButton isWorking={false} onClick={onRefChanged} />
						</span>
					}
					variant="filled"
					fullWidth
					type="text"
					value={activeTask?.reference || ""}
					inputProps={{ maxLength: MYSQL_LIMITS.VARCHAR(50).maxLength }}
					onBlur={(e) => {
						if (!e.target.value || e.target.value.trim() === "") {
							onRefChanged();

						}
					}}
					onChange={(e) => onRefChanged(e.target.value.trim())}
					disabled={!activeTask}
				/>
			</Grid>
			<Grid size={{ xs: 4, sm: 4, md: 2 }} >
				<LocalizationProvider dateAdapter={AdapterDateFns}>
					<DatePicker
						label={
							<span>
								{t("common:ui.labels.date")}&nbsp;&nbsp;
								<RefreshButton isWorking={false} onClick={onDateChanged} />
							</span>
						}
						value={activeTask?.date ? parseISODay(activeTask?.date) as Date : businessTodayDate()}
						minDate={new Date(minDate)}
						onChange={(newDate) => {
							if (!newDate || Util.compareStrings(newDate.toString(), "Invalid Date") || newDate < new Date(minDate)) {
								setErrorInput("dateInput");
								setTimeout(() => setErrorInput(""), 1500);
								return;
							}
							const date = newDate instanceof Date ? newDate : newDate.toDate(); // ✅ safe narrowing
							onDateChanged(date);
						}}
						disabled={!activeTask}
						slotProps={{
							textField: {
								id: "dateInput",
								size: "small",
								fullWidth: true,
								variant: "filled", // ✅ makes the textfield filled
								error: errorInput === "dateInput", // ✅ built-in error state
								helperText:
									errorInput === "dateInput" ? t("common:validation.invalidDate") : "", // ✅ error message
							},
						}}
					/>
				</LocalizationProvider>
			</Grid>
			<Grid size={{ xs: 4, sm: 4, md: 3 }} >
				<Autocomplete
					id="depotInput"
					size="small"
					options={depots}
					filterOptions={createFilterOptions((opt) => opt.DepotName || "")}
					getOptionLabel={(depot) => depot.DepotName || ""}
					value={depots.find((d) => d.id === activeTask?.depot) || null}
					onChange={(_, newValue) => {
						onDepotChanged(newValue?.id || -1);
					}}
					loading={isDepotLoading}
					autoHighlight
					autoComplete
					autoSelect
					disabled={!activeTask || isDepotLoading || itemsExist}
					renderInput={(params) => (
						<TextField
							{...params}
							label={
								<span>
									{t("common:ui.labels.depot")}&nbsp;&nbsp;
									<RefreshButton isWorking={isDepotLoading} onClick={fetchDepots} disabled={isDepotLoading} />
								</span>
							}
							variant="filled"
							fullWidth
						/>
					)}
				/>
			</Grid>
			<Grid size={{ xs: 4, sm: 4, md: 2 }} >
				<Autocomplete
					id="typeInput"
					size="small"
					options={types}
					filterOptions={createFilterOptions((opt) => getInventoryTypeLabel(opt))}
					getOptionLabel={(item) => getInventoryTypeLabel(item)}
					value={types.find((t) => t.key === activeTask?.invType) || null}
					onChange={(_, newValue) => updateActiveTask(setPendingTasks, { invType: newValue?.key ?? null })}
					autoHighlight
					autoComplete
					autoSelect
					disabled={!activeTask}
					renderInput={(params) => (
						<TextField
							{...params}
							label={t("common:ui.labels.inventoryType")}
							variant="filled"
							fullWidth
						/>
					)}
				/>
			</Grid>
		</Grid>
	);
};

export default InventoryDetail;